import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Shield, Brain, Code, Wrench, Loader2, ArrowRight, Sparkles, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../effects/ScrollReveal';
import CourseCard from '../shared/CourseCard';

const API_BASE = 'https://xceededge.com';

const tracks = [
  {
    icon: Shield,
    title: 'Cybersecurity',
    categoryId: 'cybersecurity',
    programs: '3 Programs',
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    border: 'group-hover:border-emerald-500/50'
  },
  {
    icon: Brain,
    title: 'AI & Future Tech',
    categoryId: 'ai-ml',
    programs: '2 Programs',
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
    border: 'group-hover:border-purple-500/50'
  },
  {
    icon: Code,
    title: 'Developer Track',
    categoryId: 'web-dev',
    programs: '3 Programs',
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    border: 'group-hover:border-blue-500/50'
  },
  {
    icon: Wrench,
    title: 'Enterprise Tools',
    categoryId: 'cloud',
    programs: '1 Program',
    color: 'text-orange-500',
    bg: 'bg-orange-500/10',
    border: 'group-hover:border-orange-500/50'
  },
];

export default function CareerPrograms() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/get-courses.php`, {
      params: {
        limit: 6,
        sort: 'popular' 
      }
    })
      .then(res => {
        if (res.data.success) setCourses(res.data.data);
        else setError('Failed to load courses.');
      })
      .catch(() => setError('Server error while loading courses.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="py-24 relative overflow-hidden bg-background">
      {/* Background Accents */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(#ffffff 1px,transparent 1px),linear-gradient(90deg,#ffffff 1px,transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      />
      <motion.div
        animate={{ x: [0, 30, 0], y: [0, -40, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none opacity-20 blur-[80px]"
        style={{ background: 'radial-gradient(circle, rgba(254,210,2,0.4) 0%, transparent 70%)' }}
      />
      <motion.div
        animate={{ x: [0, -30, 0], y: [0, 40, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none opacity-10 blur-[80px]"
        style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.4) 0%, transparent 70%)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">

        {/* Section Heading */}
        <ScrollReveal variant="fadeUp" className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-primary text-sm font-bold tracking-widest uppercase mb-4 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20">
            <Sparkles className="w-4 h-4" />
            What We Offer
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 text-foreground">
            Our Career{' '}
            <span className="text-primary relative inline-block">
              Programs
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="absolute -bottom-2 left-0 right-0 h-1.5 bg-primary rounded-full origin-left opacity-80"
              />
            </span>
          </h2>
          <p className="text-muted-foreground mt-6 max-w-2xl mx-auto text-lg font-body leading-relaxed">
            Industry-aligned tracks designed by experts to take you from beginner to job-ready professional.
          </p>
        </ScrollReveal>

        {/* Domain Tracks Mini-Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-20">
          {tracks.map((track, i) => {
            const TrackIcon = track.icon;
            return (
              <ScrollReveal key={track.title} variant="fadeUp" delay={i * 0.1}>
                <Link to={`/courses?category=${track.categoryId}`}>
                  <motion.div
                    whileHover={{ y: -5 }}
                    whileTap={{ scale: 0.98 }}
                    className={`group bg-card/50 backdrop-blur-sm rounded-2xl border border-border/50 p-6 cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-background/50 ${track.border}`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${track.bg}`}>
                      <TrackIcon className={`w-6 h-6 ${track.color}`} />
                    </div>
                    <h4 className="font-heading font-bold text-foreground text-lg mb-1 group-hover:text-primary transition-colors">
                      {track.title}
                    </h4>
                    <p className="text-sm text-muted-foreground font-medium">
                      {track.programs}
                    </p>
                  </motion.div>
                </Link>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Featured Courses Header */}
<div className="flex flex-col sm:flex-row sm:items-center justify-between mb-10 gap-6 border-b border-border/10 pb-6">
  
  {/* Title Area */}
  <div className="flex items-center gap-4">
    {/* Animated Trending Icon */}
    <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0 shadow-inner">
      <Flame className="w-6 h-6 text-orange-500 animate-pulse" />
    </div>
    
    <div>
      <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
        Trending <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-400">Courses</span>
      </h3>
      <p className="text-muted-foreground text-sm mt-1">
        Most popular programs among our students
      </p>
    </div>
  </div>

  {/* Call to Action Button */}
  <Link 
    to="/courses" 
    className="hidden sm:inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-card border border-border/50 text-foreground font-semibold text-sm hover:border-primary/50 hover:bg-primary/5 hover:shadow-[0_0_15px_rgba(254,210,2,0.1)] transition-all duration-300 group"
  >
    Explore All 
    <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
  </Link>
  
</div>

        {/* Dynamic Courses Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-card/20 rounded-3xl border border-border/50">
            <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground font-medium animate-pulse">Loading top courses...</p>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-20 bg-red-500/5 rounded-3xl border border-red-500/20">
            <p className="text-red-500 font-medium">{error}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {courses.map((course, i) => (
              <CourseCard key={course.id} course={course} index={i} />
            ))}
          </div>
        )}

        {/* Mobile View All Button */}
        <div className="mt-10 text-center sm:hidden">
          <Link 
            to="/courses" 
            className="inline-flex items-center justify-center gap-2 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground border border-primary/20 font-semibold px-6 py-3 rounded-xl transition-all w-full"
          >
            Explore All Courses
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

      </div>
    </section>
  );
}